<!--
  Static study-overview content for the enrollment page: what the study
  involves, the two parts, compensation, and privacy. Pulled out of the ERB
  view into its own component (mounted the same way as enrollment_form, via
  app/javascript/mount.js) so it can be reused wherever this overview is
  needed, independent of the interactive enrollment_form below it.
-->
<header class="text-centerx">
  <h1 class="text-3xl font-bold text-gray-900">Welcome to the SONORE Research Study Enrollment Website!</h1>
  <p class="mt-4 text-gray-700 leading-relaxed">
    We are currently recruiting speakers of French, Arabic, and English for a language-related
    research study. Participation in the study is voluntary, and involves completion of up to two
    parts as described below. Participants may participate in either part or both parts if you meet
    the requirements. That is, you may choose to do only Part 1, or only Part 2, or you may choose
    to do both Part 1 and Part 2.
  </p>
</header>

<div class="mt-12 grid gap-6 sm:grid-cols-2">
  <section class="rounded-xl bg-indigo-50 ring-1 ring-indigo-200 p-6">
    <span class="inline-block rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
      Part 1
    </span>
    <h2 class="mt-3 text-2xl font-semibold text-gray-900">Audio and Video</h2>
    <p class="mt-2 text-gray-700 leading-relaxed">
      You will engage in eleven 8&ndash;10-minute recorded phone calls with acquaintances on a topic
      of your choosing; and you will produce four 3&ndash;10-minute videos of monologue speech on a
      topic of your choosing (note that you do not have to be visible in the video). Six of these
      calls will be in your primary language (French or Arabic); five of these calls will be in your
      secondary language (either non-native English, non-native Arabic, or
      non-native French).
    </p>
  </section>

  <section class="rounded-xl bg-indigo-50 ring-1 ring-indigo-200 p-6">
    <span class="inline-block rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
      Part 2
    </span>
    <h2 class="mt-3 text-2xl font-semibold text-gray-900">In-Person</h2>
    <p class="mt-2 text-gray-700 leading-relaxed">
      You will attend three in-person recording sessions at 11 rue des Cordeli&egrave;res, 75013
      Paris, France on different days. During each session, you will
      record one informal conversation (15 minutes) and one telephone call (10 minutes), for a
      total of three conversations and three calls. These conversations may take place with an
      acquaintance, or if needed, a trained staff member. Two of these sessions will be in French,
      and one session will be in either Arabic or English.
    </p>
  </section>
</div>

<section class="mt-8 space-y-4 text-gray-700 leading-relaxed">
  <p>
    You will be asked to provide certain demographic information &mdash; specifically, year of
    birth, sex assigned to you at birth, and what languages you speak. You will be asked to provide
    your name and email address for compensation, contact, and identification purposes.  You will be asked for a password
    for the purpose of signing in to this website.
  </p>
  <p>
    <b>To participate in this study, you must be at least 18 years old and meet the language
    requirements of the study. For Part 1, you must be a multilingual speaker, highly fluent in
    Arabic or French. For Part 2, you must be highly fluent in French.</b>
  </p>
  <p>
    This data is being collected by the Linguistic Data Consortium (LDC) at the University of
    Pennsylvania, Philadelphia, Pennsylvania USA and Evaluations and Language Resources
    Distribution Agency (ELDA) to support language-related research, education and technology
    development. The data you contribute will be collected, processed and transferred to the United
    States for the legitimate interest of research.
  </p>
  <p>
    The recordings, demographic information and non-identifying recording metadata including date,
    time and duration will be stored indefinitely on limited access secure servers at LDC. Your
    name and email address are collected for compensation, contact, and identification purposes only. This
    information is stored separately from the collected linguistic data samples for up to 7 years
    after the research study is completed in a password-protected database that can be accessed by
    authorized study personnel only.
  </p>
  <p>
    The recordings, demographic information and non-identifying recording metadata including date,
    time and duration will be included in a database that will be shared by LDC with individuals
    and organizations engaged in language-related research, education and technology development.
    Individuals and organizations that receive this material will first agree to use these
    resources only for language-related research, education and technology development purposes.
    Names and emails collected from participants for compensation, contact, and identification purposes are not
    distributed to individuals or organizations receiving the data. While unlikely, it is possible
    that participants could be re-identified because recordings will include voiceprints.
  </p>
  <p>This research is unlikely to cause participants to object or complain.</p>
  <p>
    You have the right to request access, rectification, erasure or restriction of data processing,
    to object to data processing, and to exercise the right to data portability. You can leave the
    study or ask us to remove your data from our database at any time. Contact us regarding these
    matters at
    <a href="mailto:sonoreetude@gmail.com" class="text-indigo-600 hover:text-indigo-500">sonoreetude@gmail.com</a>.
  </p>
</section>

<hr class="my-12 border-gray-200">

<h2 class="text-2xl font-bold text-gray-900 mb-6">What exactly will I have to do?</h2>

<section class="mt-8">
  <h3 class="text-xl font-semibold text-gray-900 mb-3">1. Enroll in the study</h3>
  <p class="text-gray-700 leading-relaxed mb-3">
    Participants can enroll for the study using our secure website. Enrollment involves these steps:
  </p>
  <ul class="list-disc list-outside pl-5 space-y-1 text-gray-700">
    <li>Provide your name and email so that we can contact you about the study and compensate you for your time</li>
    <li>Provide a password so you can sign in to this website, using your email address</li>
    <li>Provide demographic information including language, year of birth, and sex assigned at birth</li>
    <li>Provide consent for the data you contribute to be used in our research database</li>
  </ul>
  <p class="text-gray-700 leading-relaxed mt-3">
    Upon enrollment, you will be assigned a Personal Identification Number (PIN) which you will use
    during the phone calls, video recordings, and in-person sessions. You will also be sent a
    confirmation email with information about the study.
  </p>
</section>

<!--
  The full task instructions live on their own page (/instructions) so this
  overview stays readable. Each item below links to its section there; the
  anchors are defined in app/views/pages/instructions.html.erb.
-->
{#snippet moreLink(href, label)}
  <p class="mt-3">
    <a class="text-indigo-600 hover:text-indigo-500 font-medium" {href}>{label} &rarr;</a>
  </p>
{/snippet}

<section class="mt-10">
  <h3 class="text-xl font-semibold text-gray-900 mb-3">2. If participating in Part 1, complete your phone calls</h3>
  <p class="text-gray-700 leading-relaxed">
    You will make eleven phone calls to people you know, like friends, family members, neighbors,
    and colleagues. Six are in your assigned primary language and five in your assigned secondary
    language, each lasting 8&ndash;10 minutes.
  </p>
  {@render moreLink('/instructions#phone-calls', 'Read the full instructions for your phone calls')}
</section>

<section class="mt-10">
  <h3 class="text-xl font-semibold text-gray-900 mb-3">3. If participating in Part 1, record and upload your videos</h3>
  <p class="text-gray-700 leading-relaxed">
    You will record and upload four short videos of yourself talking spontaneously on any topic you
    like &mdash; three in your primary language and one in your secondary language, each 3&ndash;10
    minutes. Alternatively, if you have existing video recordings that meet the study requirements,
    you can use those instead of making new ones.
  </p>
  {@render moreLink('/instructions#videos', 'Read the full instructions for your video uploads')}
</section>

<section class="mt-10">
  <h3 class="text-xl font-semibold text-gray-900 mb-3">4. If participating in Part 2, attend in-person sessions</h3>
  <p class="text-gray-700 leading-relaxed">
    You will attend three in-person sessions on different days. Each session has two components:
    one in-person conversation and one telephone call. For both, you and your conversation partner
    will wear a headset and a clip-on microphone.
  </p>
  {@render moreLink('/instructions#in-person', 'Read the full instructions for your in-person sessions')}
</section>

<section class="mt-10">
  <div class="rounded-md bg-indigo-50 ring-1 ring-indigo-200 p-4 text-gray-800">
    If you have any problems, email your recruiter.
  </div>
</section>

<hr class="my-12 border-gray-200">

<section class="mt-8">
  <h2 class="text-2xl font-semibold text-gray-900 mb-3">Compensation</h2>
  <p class="text-gray-700 leading-relaxed mb-3">
    Study personnel will conduct a number of automatic and manual checks on your recordings to
    ensure that they meet study requirements. You will receive compensation for successful
    recordings, as follows:
  </p>
  <ul class="list-disc list-outside pl-5 space-y-1 text-gray-700">
    <li>complete Part 1 &ndash; [$X]</li>
    <li>complete Part 2 &ndash; [$X]</li>
    <li>complete Part 1 and Part 2 &ndash; [$X]</li>
  </ul>
  <p class="text-gray-700 leading-relaxed mt-3">The maximum amount you can earn is [$X].</p>
</section>

<section class="mt-10 space-y-4 text-gray-700 leading-relaxed">
  <h2 class="text-2xl font-semibold text-gray-900 mb-1">Privacy</h2>
  <p>
    Protecting your privacy is very important to us. To date we have conducted more than 60
    language-related studies all over the world.
  </p>
  <p>
    Our research database will include your phone call and video recordings with automatically
    generated metadata including the recording time, date and duration. Each recording will be
    linked to a pseudonymous subject ID along with the demographic information (sex, year of birth
    and language) you provided during enrollment.
  </p>
  <p>
    The distributed database does not include your name, email, or other personal data we collect
    in order to contact and compensate you during the study. This information is only accessible to
    study personnel and is stored in a secure database.
  </p>
  <p>
    The recordings, demographic information and non-identifying recording metadata including date,
    time and duration included in the research database will be shared by LDC with individuals and
    organizations engaged in language-related research, education and technology development.
    Individuals and organizations that receive this material will first agree to use these resources
    only for language-related research, education and technology development purposes. Names and
    emails collected from participants for compensation and contact purposes are not distributed to
    individuals or organizations receiving the data.
  </p>
  <p>
    You will not be identified by name, email address or other personal identifier in any data sets,
    publications or presentations that may result from this research.
  </p>
  <p>
    The study website is subject to the University of Pennsylvania&rsquo;s privacy policy,
    <a href="https://www.upenn.edu/about/privacy-policy" class="text-indigo-600 hover:text-indigo-500" target="_blank" rel="noopener">https://www.upenn.edu/about/privacy-policy</a>.
  </p>
  <p>
    You can leave the study or ask us to remove your data from our research database at any time by
    contacting us at
    <a href="mailto:sonoreetude@gmail.com" class="text-indigo-600 hover:text-indigo-500">sonoreetude@gmail.com</a>.
  </p>
  <p>
    For information regarding your rights as a research subject, you may contact the Director of
    Regulatory Affairs at the University of Pennsylvania by telephoning +1 215-898-2614. If you have
    any questions pertaining to your participation in this research study, you may contact the
    primary investigator by sending an email to
    <a href="mailto:sonoreetude@gmail.com" class="text-indigo-600 hover:text-indigo-500">sonoreetude@gmail.com</a>.
  </p>
</section>

<hr class="my-12 border-gray-200">
